<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useProjeto } from '@/modules/projeto/composables/useProjeto';

const props = defineProps({
    projetoId: String,
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: Number,
        default: null
    },
    titulo: {
        type: String,
        default: 'Conta Bancária'
    },
    placeholder: {
        type: String,
        default: 'Selecione a conta bancária'
    },
    obrigatorio: {
        type: Boolean,
        default: true
    },
    // Ids de contas que não devem aparecer na lista (ex.: conta já escolhida como origem).
    idsIgnorados: {
        type: Array,
        default: () => []
    },
    classeColuna: {
        type: String,
        default: 'col-12 md:col-6'
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const { contasBancarias, carregarContasBancarias } = useProjeto();

const contaSelecionada = ref(null);

const contasDisponiveis = computed(() =>
    contasBancarias.value.filter((conta) => !props.idsIgnorados.includes(conta.id))
);

const rotuloConta = (conta) => {
    if (!conta) return '';
    const agencia = conta.agencia_dv ? `${conta.agencia}-${conta.agencia_dv}` : conta.agencia;
    const operacao = conta.operacao ? ` • Operação ${conta.operacao}` : '';
    return `Agência ${agencia}, Conta ${conta.conta}-${conta.conta_dv}${operacao}`;
};

const sincronizaSelecao = () => {
    if (!props.modelValue) {
        contaSelecionada.value = null;
        return;
    }

    const conta = contasBancarias.value.find((item) => item.id === props.modelValue) || null;
    if (contaSelecionada.value?.id !== conta?.id) {
        contaSelecionada.value = conta;
    }
};

const selecionaConta = () => {
    emit('update:modelValue', contaSelecionada.value ? contaSelecionada.value.id : null);
    emit('change', contaSelecionada.value);
};

watch(() => props.modelValue, sincronizaSelecao);

watch(() => props.projetoId, async (novoProjetoId) => {
    if (!novoProjetoId) {
        contaSelecionada.value = null;
        return;
    }

    await carregarContasBancarias(novoProjetoId);
    sincronizaSelecao();
});

// Limpa a seleção caso a conta escolhida passe a ser ignorada.
watch(contasDisponiveis, (contas) => {
    if (contaSelecionada.value && !contas.some((conta) => conta.id === contaSelecionada.value.id)) {
        contaSelecionada.value = null;
        selecionaConta();
    }
});

onMounted(async () => {
    if (props.projetoId) {
        await carregarContasBancarias(props.projetoId);
        sincronizaSelecao();
    }
});
</script>

<template>
    <div class="field mb-2" :class="props.classeColuna">
        <SmallTitle :titulo="props.titulo" :obrigatorio="props.obrigatorio" />
        <Dropdown v-model="contaSelecionada" :options="contasDisponiveis" filter showClear
            emptyFilterMessage="Nenhum item encontrado" emptyMessage="Nenhuma conta bancária encontrada"
            :placeholder="props.placeholder" @change="selecionaConta"
            :pt="{ input: { class: 'text-sm' }, item: { class: 'text-sm' } }" :class="{ 'p-invalid': props.erro }"
            class="w-full">
            <template #value="slotProps">
                <Transition name="conta-valor" mode="out-in">
                    <div v-if="slotProps.value" :key="slotProps.value.id" class="conta-valor">
                        <span class="font-semibold text-sm text-800 line-height-2">
                            {{ slotProps.value.banco_nome }}
                        </span>
                        <span class="text-xs text-500 line-height-2">{{ rotuloConta(slotProps.value) }}</span>
                    </div>
                    <span v-else key="placeholder" class="text-sm line-height-2">
                        {{ slotProps.placeholder }}
                    </span>
                </Transition>
            </template>

            <template #option="slotProps">
                <div class="flex align-items-center gap-2 w-full">
                    <div class="flex flex-column gap-1">
                        <span class="font-semibold text-sm line-height-2">{{ slotProps.option.banco_nome }}</span>
                        <span class="text-xs text-500 line-height-2">{{ rotuloConta(slotProps.option) }}</span>
                    </div>
                    <TagBase v-if="slotProps.option.contabancaria_id == null" value="Desvinculada" cor="vermelho"
                        negrito classe="ml-auto" />
                </div>
            </template>
        </Dropdown>
        <SmallError v-if="props.erro" :error="props.erro" />
    </div>
</template>

<style scoped>
/*
 * O campo vazio ocupa uma linha e o preenchido, duas. Em vez de reservar a altura
 * maior o tempo todo, a segunda linha é animada de 0fr para 1fr ao selecionar.
 */
.conta-valor {
    display: grid;
    grid-template-rows: auto 1fr;
}

.conta-valor > * {
    overflow: hidden;
}

.conta-valor-enter-active,
.conta-valor-leave-active {
    transition: grid-template-rows 200ms ease, opacity 150ms ease;
}

.conta-valor-enter-from,
.conta-valor-leave-to {
    grid-template-rows: auto 0fr;
    opacity: 0;
}
</style>
